import React from 'react';
import {
  Layers,
  Percent,
  TrendingUp,
  Ticket,
  IndianRupee,
  Loader2,
  AlertCircle,
  MapPin,
  Building2,
  RefreshCw,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useMerchantCommissionReport } from '@/hooks/useVendors';
import { Merchant, CommissionReportItem } from '@/api/vendors';
import { AxiosError } from 'axios';

interface VendorReferralConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor?: Merchant | null;
}

export function VendorReferralConfigModal({
  open,
  onOpenChange,
  vendor,
}: VendorReferralConfigModalProps) {
  const {
    data: reportData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useMerchantCommissionReport(vendor?.id, open);

  const rawList: CommissionReportItem[] = Array.isArray(reportData)
    ? reportData
    : Array.isArray(reportData?.data)
      ? reportData.data
      : [];

  const singleItem =
    reportData && !Array.isArray(reportData) && 'merchantId' in reportData
      ? (reportData as unknown as CommissionReportItem)
      : undefined;

  const reportItem: CommissionReportItem | undefined =
    rawList.find((d) => Number(d?.merchantId) === Number(vendor?.id)) || rawList[0] || singleItem;

  let parsedSplits: Record<string, number> = {};
  if (reportItem?.layerSplits) {
    if (typeof reportItem.layerSplits === 'string') {
      try {
        parsedSplits = JSON.parse(reportItem.layerSplits);
      } catch {
        parsedSplits = {};
      }
    } else if (typeof reportItem.layerSplits === 'object') {
      parsedSplits = reportItem.layerSplits;
    }
  }

  const layerEntries = Object.entries(parsedSplits).sort(([a], [b]) => {
    const numA = parseInt(a.replace(/\D/g, ''), 10) || 0;
    const numB = parseInt(b.replace(/\D/g, ''), 10) || 0;
    return numA - numB;
  });

  const totalSplit = layerEntries.reduce((sum, [, val]) => sum + (Number(val) || 0), 0);
  const maxSplit = Math.max(...layerEntries.map(([, v]) => Number(v) || 0), 25);

  const axiosErr = error as AxiosError<{ message?: string }> | null;
  const errorMessage =
    axiosErr?.response?.data?.message ||
    axiosErr?.message ||
    'Failed to load commission configuration';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b bg-muted/20">
          <div className="flex items-center justify-between pr-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  Referral Commission Config
                  {reportItem && (
                    <span
                      className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                        reportItem.isCustomVoucherRedeemConfig
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                      }`}
                    >
                      {reportItem.isCustomVoucherRedeemConfig
                        ? 'Custom Voucher Config'
                        : 'Standard Config'}
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  <span className="font-semibold text-foreground flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    {reportItem?.merchantName || vendor?.name || 'Vendor'}
                  </span>
                  {(reportItem?.city || vendor?.city) && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                      {reportItem?.city || vendor?.city}
                      {(reportItem?.state || vendor?.state) &&
                        `, ${reportItem?.state || vendor?.state}`}
                    </span>
                  )}
                  {vendor?.id && <span className="text-muted-foreground">ID: #{vendor.id}</span>}
                </DialogDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 ml-auto"
              onClick={() => refetch()}
              disabled={isLoading || isFetching}
              title="Refresh config data"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </DialogHeader>

        {/* Body Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm font-medium text-muted-foreground">
                Fetching referral & commission configuration...
              </p>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <AlertCircle className="h-10 w-10 text-destructive mb-3" />
              <p className="text-base font-semibold text-foreground">
                Failed to load commission configuration
              </p>
              <p className="text-xs text-destructive mt-2 max-w-md font-mono bg-destructive/10 p-2 rounded">
                {errorMessage}
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : !reportItem ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
              <Layers className="h-10 w-10 stroke-1 mb-2 opacity-50" />
              <p className="text-sm font-medium">No referral configuration found</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                No commission configuration was returned for this merchant.
              </p>
            </div>
          ) : (
            <>
              {/* Stat Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-xl border bg-card p-3.5 shadow-sm">
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="text-xs font-medium">Effective Share</span>
                    <Percent className="h-4 w-4 text-primary" />
                  </div>
                  <div className="mt-2 text-2xl font-bold text-foreground">
                    {reportItem.effectiveSharePercentage}%
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Base share rate</p>
                </div>

                <div className="rounded-xl border bg-card p-3.5 shadow-sm">
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="text-xs font-medium">Total Redemptions</span>
                    <Ticket className="h-4 w-4 text-blue-500" />
                  </div>
                  <div className="mt-2 text-2xl font-bold text-foreground">
                    {reportItem.totalRedemptions?.toLocaleString('en-IN') ?? 0}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Completed vouchers</p>
                </div>

                <div className="rounded-xl border bg-card p-3.5 shadow-sm">
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="text-xs font-medium">Gross Sales</span>
                    <IndianRupee className="h-4 w-4 text-emerald-500" />
                  </div>
                  <div
                    className="mt-2 text-xl font-bold text-foreground truncate"
                    title={`₹${reportItem.grossSalesAmount?.toLocaleString('en-IN') ?? 0}`}
                  >
                    ₹{reportItem.grossSalesAmount?.toLocaleString('en-IN') ?? 0}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Total transaction volume
                  </p>
                </div>

                <div className="rounded-xl border bg-card p-3.5 shadow-sm">
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span className="text-xs font-medium">Upline Commission</span>
                    <TrendingUp className="h-4 w-4 text-violet-500" />
                  </div>
                  <div
                    className="mt-2 text-xl font-bold text-foreground truncate"
                    title={`₹${reportItem.totalUplineCommissionDistributed?.toLocaleString('en-IN') ?? 0}`}
                  >
                    ₹{reportItem.totalUplineCommissionDistributed?.toLocaleString('en-IN') ?? 0}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Distributed to network</p>
                </div>
              </div>

              {/* Referral Layers Section */}
              <div className="rounded-xl border bg-card p-4 shadow-sm space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b">
                  <div>
                    <h3 className="text-sm font-bold tracking-tight text-foreground flex items-center gap-2">
                      <Layers className="h-4 w-4 text-primary" />
                      Referral Layer Splits
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Percentage share distributed across upline referral layers (L1 – L
                      {layerEntries.length || 20})
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-md bg-muted border text-foreground">
                      Total Split: <strong className="text-primary">{totalSplit}%</strong>
                    </span>
                    <span className="text-xs font-medium text-muted-foreground">
                      {layerEntries.length} {layerEntries.length === 1 ? 'Layer' : 'Layers'}
                    </span>
                  </div>
                </div>

                {layerEntries.length === 0 ? (
                  <p className="text-sm text-center py-6 text-muted-foreground">
                    No layer splits defined in this configuration.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
                    {layerEntries.map(([layerKey, val]) => {
                      const splitVal = Number(val) || 0;
                      const barPercent = Math.min(100, Math.max(0, (splitVal / maxSplit) * 100));

                      return (
                        <div
                          key={layerKey}
                          className="flex flex-col justify-between p-2.5 rounded-lg border bg-slate-50/50 dark:bg-slate-900/50 border-border hover:border-primary/40 transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-400 bg-slate-200/60 dark:bg-slate-800 px-2 py-0.5 rounded">
                              {layerKey}
                            </span>
                            <span className="text-sm font-extrabold text-foreground">
                              {splitVal}%
                            </span>
                          </div>

                          <div className="mt-2.5 h-1.5 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all duration-300"
                              style={{ width: `${barPercent}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="p-4 border-t bg-muted/20 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
